import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';
import { toast } from 'sonner';

export function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Détecter iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Vérifier si déjà installé
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Écouter l'installation réussie
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
      toast.success('Application installée avec succès ! ');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('Installation en cours...');
    } else {
      toast.info('Installation annulée');
    }
    
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleIOSInstall = () => {
    toast(
      <div className="space-y-2">
        <p className="font-semibold">Pour installer sur iOS :</p>
        <ol className="list-decimal list-inside space-y-1 text-sm">
          <li>Appuyez sur le bouton Partager <span className="inline-block">↗️</span></li>
          <li>Faites défiler et appuyez sur "Sur l'écran d'accueil"</li>
          <li>Appuyez sur "Ajouter"</li>
        </ol>
      </div>,
      {
        duration: 10000,
      }
    );
  };

  if (isInstalled || (!showBanner && !isIOS)) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50">
      <div className="bg-background rounded-lg shadow-xl p-4 border border-border">
        <button
          onClick={() => setShowBanner(false)}
          className="absolute top-2 right-2 p-1 hover:bg-accent rounded"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <img src="/icon-96x96.png" alt="NoCodeo" className="w-12 h-12 rounded-lg" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">
              Installer NoCodeo
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Accédez rapidement à vos formations depuis votre écran d'accueil
            </p>
            
            <Button
              onClick={isIOS ? handleIOSInstall : handleInstall}
              className="mt-3 w-full"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              {isIOS ? "Comment installer" : "Installer l'application"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
